import {FilterCriteria} from '../../filter/api/filter.criteria';
import type {MetaDataAttributeDto} from '../../meta/dto/meta-data-attribute.dto';
import {MetaDataAttributeKind} from '../../meta/logic/meta-data-attribute-kind';
import {QlBuilderRelativeDateBase} from './model/ql-builder-relative-date-base.type';
import {QlBuilderRelativeDateUnit} from './model/ql-builder-relative-date-unit.type';
import {QlBuilderRelativeDateExpression} from './model/ql-builder-relative-date-expression.interface';
import {QlBuilderDateFieldResolver} from './model/ql-builder-date-field-resolver.type';

/**
 * A relative date filter value — the ONE `${…}` form the backend policy admits in a widget definition
 * (`UiQlWidgetDefinitionPolicy#isAllowedFilterVariable`): the WHOLE value is a date runtime expression
 * `${D-<BASE>[±P…]}`, parsed by `core.runtime.DateRuntimeDataProvider`
 * (`\$\{D-([A-Z]+)([+-]P\d+Y?\d*M?\d*D?)?}` + a known baseline + `java.time.Period.parse`).
 *
 * Group 1 base, group 2 sign, groups 3–5 years/months/days. After the sign at least one component is
 * required (`P5` fails `Period.parse` backend-side), there are no weeks (`P2W` fails the engine regex)
 * and no time part (`PT…`). Components are capped at 9 digits so `Period.parse` cannot overflow `int`.
 */
const RELATIVE_DATE_BASES = 'TODAY|STARTMONTH|STARTYEAR|ENDMONTH|ENDYEAR|STARTWEEK|ENDWEEK';
const RELATIVE_DATE_PERIOD = String.raw`([+-])P(?=\d)(?:(\d{1,9})Y)?(?:(\d{1,9})M)?(?:(\d{1,9})D)?`;
const RELATIVE_DATE_EXPRESSION = new RegExp(String.raw`^\$\{D-(${RELATIVE_DATE_BASES})(?:${RELATIVE_DATE_PERIOD})?\}$`);

/** Java types the backend `DateRuntimeDataProvider.isRelevant` accepts, as `MetaDataAttributeDto.dataType` names them. */
const DATE_DATA_TYPES = new Set<string>([
    'java.time.LocalDate', 'java.time.LocalDateTime', 'java.time.Instant',
    'LocalDate', 'LocalDateTime', 'Instant',
]);

/** Comparison operators the editor offers for a relative date. */
const OPERATORS: { value: string; symbol: string; label: string }[] = [
    {value: FilterCriteria.cOperatorGreaterEqual, symbol: '≥', label: 'ab (≥)'},
    {value: FilterCriteria.cOperatorGreaterThan, symbol: '>', label: 'nach (>)'},
    {value: FilterCriteria.cOperatorLowerEqual, symbol: '≤', label: 'bis (≤)'},
    {value: FilterCriteria.cOperatorLowerThan, symbol: '<', label: 'vor (<)'},
    {value: FilterCriteria.cOperatorEqual, symbol: '=', label: 'am (=)'},
];

/** Base labels inside a sentence ("≥ heute − 30 Tage"). */
const BASE_TEXT: { [base in QlBuilderRelativeDateBase]: string } = {
    TODAY: 'heute',
    STARTMONTH: 'Monatsanfang',
    STARTYEAR: 'Jahresanfang',
    ENDMONTH: 'Monatsende',
    ENDYEAR: 'Jahresende',
    STARTWEEK: 'Wochenanfang',
    ENDWEEK: 'Wochenende',
};

/**
 * Pure, Angular-free handling of relative date filter values for the QL-Widget-Builder: parse, emit,
 * describe as German text, and the date-field check the client precheck mirrors from the backend.
 */
export class QlBuilderRelativeDate {

    /**
     * The bases the builder OFFERS. STARTWEEK/ENDWEEK are parsed (the engine knows them and the
     * condition builder emits them) but deliberately not offered.
     */
    static readonly BASE_OPTIONS: { label: string; value: QlBuilderRelativeDateBase }[] = [
        {label: 'Heute', value: 'TODAY'},
        {label: 'Monatsanfang', value: 'STARTMONTH'},
        {label: 'Jahresanfang', value: 'STARTYEAR'},
        {label: 'Monatsende', value: 'ENDMONTH'},
        {label: 'Jahresende', value: 'ENDYEAR'},
    ];

    static readonly DIRECTION_OPTIONS: { label: string; value: '+' | '-' }[] = [
        {label: '−', value: '-'},
        {label: '+', value: '+'},
    ];

    static readonly UNIT_OPTIONS: { label: string; value: QlBuilderRelativeDateUnit }[] = [
        {label: 'Tage', value: 'D'},
        {label: 'Wochen', value: 'W'},
        {label: 'Monate', value: 'M'},
        {label: 'Jahre', value: 'Y'},
    ];

    static readonly OPERATOR_OPTIONS: { label: string; value: string }[] =
        OPERATORS.map(o => ({label: o.label, value: o.value}));

    /** Upper bound of the editor's number input (stays far below the 9-digit cap of the pattern). */
    static readonly MAX_AMOUNT = 9999;

    // ------------------------------------------------------------------
    // parse / emit
    // ------------------------------------------------------------------

    /** Whether the WHOLE value is a relative date expression the engine accepts. */
    static isExpression(value: unknown): value is string {
        return typeof value === 'string' && RELATIVE_DATE_EXPRESSION.test(value);
    }

    /** Parses `${D-TODAY-P30D}` → `{base: 'TODAY', direction: '-', years: 0, months: 0, days: 30}`; `null` if not one. */
    static parse(value: unknown): QlBuilderRelativeDateExpression | null {
        if (typeof value !== 'string') {
            return null;
        }
        const match = RELATIVE_DATE_EXPRESSION.exec(value);
        if (!match) {
            return null;
        }
        return {
            base: match[1] as QlBuilderRelativeDateBase,
            direction: match[2] === '+' ? '+' : '-',
            years: Number(match[3] ?? 0),
            months: Number(match[4] ?? 0),
            days: Number(match[5] ?? 0),
        };
    }

    /** The filter value for an expression; without an offset the bare base (`${D-TODAY}`). */
    static toValue(expression: QlBuilderRelativeDateExpression): string {
        const years = QlBuilderRelativeDate.component(expression.years);
        const months = QlBuilderRelativeDate.component(expression.months);
        const days = QlBuilderRelativeDate.component(expression.days);
        if (!years && !months && !days) {
            return `\${D-${expression.base}}`;
        }
        const period = (years ? `${years}Y` : '') + (months ? `${months}M` : '') + (days ? `${days}D` : '');
        return `\${D-${expression.base}${expression.direction}P${period}}`;
    }

    /**
     * The expression of the editor's single "number + unit" period. Weeks become days — the engine
     * has no week unit (`2 Wochen` → `P14D`).
     */
    static fromAmount(base: QlBuilderRelativeDateBase,
                      direction: '+' | '-',
                      amount: number | null | undefined,
                      unit: QlBuilderRelativeDateUnit): QlBuilderRelativeDateExpression {
        const n = QlBuilderRelativeDate.component(amount);
        return {
            base,
            direction,
            years: unit === 'Y' ? n : 0,
            months: unit === 'M' ? n : 0,
            days: unit === 'D' ? n : unit === 'W' ? n * 7 : 0,
        };
    }

    /**
     * The editor's "number + unit" for an expression; `null` for a compound period (`P1Y2M`), which
     * the single-unit editor cannot represent. Days are read back as days (`P14D` → 14 Tage).
     */
    static toAmount(expression: QlBuilderRelativeDateExpression): { amount: number; unit: QlBuilderRelativeDateUnit } | null {
        const parts = [
            {amount: expression.years, unit: 'Y' as QlBuilderRelativeDateUnit},
            {amount: expression.months, unit: 'M' as QlBuilderRelativeDateUnit},
            {amount: expression.days, unit: 'D' as QlBuilderRelativeDateUnit},
        ].filter(part => part.amount > 0);
        if (parts.length > 1) {
            return null;
        }
        return parts[0] ?? {amount: 0, unit: 'D'};
    }

    // ------------------------------------------------------------------
    // criteria
    // ------------------------------------------------------------------

    /**
     * Whether a criterion is a plain relative date comparison the builder shows as a chip: one field,
     * one of the offered operators, a relative date value, no range, no nested group.
     */
    static isRelativeCriterion(criterion: FilterCriteria | null | undefined): boolean {
        return !!criterion
            && !!criterion.field
            && !criterion.and?.length
            && !criterion.or?.length
            && (criterion.valueTo === null || criterion.valueTo === undefined)
            && OPERATORS.some(o => o.value === criterion.operation)
            && QlBuilderRelativeDate.isExpression(criterion.value);
    }

    /** The criterion `<alias>.<field> <operation> ${D-…}`. */
    static createCriterion(qualifiedField: string,
                           operation: string,
                           expression: QlBuilderRelativeDateExpression): FilterCriteria {
        return FilterCriteria.create(qualifiedField, operation, QlBuilderRelativeDate.toValue(expression));
    }

    // ------------------------------------------------------------------
    // German text
    // ------------------------------------------------------------------

    /** "heute − 30 Tage", "Jahresanfang + 1 Jahr 2 Monate", "Monatsende". */
    static describe(expression: QlBuilderRelativeDateExpression): string {
        const parts: string[] = [];
        const add = (amount: number, singular: string, plural: string): void => {
            if (amount > 0) {
                parts.push(`${amount} ${amount === 1 ? singular : plural}`);
            }
        };
        add(expression.years, 'Jahr', 'Jahre');
        add(expression.months, 'Monat', 'Monate');
        add(expression.days, 'Tag', 'Tage');

        const base = BASE_TEXT[expression.base];
        if (!parts.length) {
            return base;
        }
        return `${base} ${expression.direction === '-' ? '−' : '+'} ${parts.join(' ')}`;
    }

    /** Chip text of a relative criterion, e.g. "Erstellt am ≥ heute − 30 Tage"; `null` if it is none. */
    static describeCriterion(criterion: FilterCriteria, fieldLabel: string): string | null {
        const expression = QlBuilderRelativeDate.isRelativeCriterion(criterion)
            ? QlBuilderRelativeDate.parse(criterion.value)
            : null;
        if (!expression) {
            return null;
        }
        const symbol = OPERATORS.find(o => o.value === criterion.operation)?.symbol ?? criterion.operation;
        return `${fieldLabel} ${symbol} ${QlBuilderRelativeDate.describe(expression)}`;
    }

    // ------------------------------------------------------------------
    // date fields
    // ------------------------------------------------------------------

    /** Whether a meta `dataType` (`java.time.LocalDate`, …) is one the backend accepts a relative date on. */
    static isDateDataType(dataType: string | null | undefined): boolean {
        return !!dataType && DATE_DATA_TYPES.has(dataType);
    }

    /**
     * A {@link QlBuilderDateFieldResolver} from the metadata attributes of each node alias. An attribute
     * is registered under every QL name it can appear as — the plain name, `g_<genericAttributeId>:value`
     * and `o_<attributeName>:value` — so the answer does not depend on how `attributeType` was delivered.
     * Unknown aliases and names resolve to `null` (unknown).
     */
    static dateFieldResolver(
        attributesByAlias: { [alias: string]: MetaDataAttributeDto[] | null | undefined }
            | ReadonlyMap<string, MetaDataAttributeDto[] | null | undefined>): QlBuilderDateFieldResolver {
        const byAlias = new Map<string, Map<string, boolean>>();
        // one resolver for EVERY node alias of a definition (root + joins): a plain object or a Map, alias -> attributes
        const entries: [string, MetaDataAttributeDto[] | null | undefined][] = attributesByAlias instanceof Map
            ? Array.from(attributesByAlias.entries())
            : Object.entries(attributesByAlias ?? {});
        for (const [alias, attributes] of entries) {
            const names = new Map<string, boolean>();
            for (const attribute of attributes ?? []) {
                if (!attribute?.attributeName) {
                    continue;
                }
                const isDate = QlBuilderRelativeDate.isDateDataType(attribute.dataType);
                names.set(attribute.attributeName, isDate);
                names.set(`o_${attribute.attributeName}:value`, isDate);
                const genericId = MetaDataAttributeKind.genericAttributeIdOf(attribute);
                if (genericId != null) {
                    names.set(`g_${genericId}:value`, isDate);
                }
            }
            byAlias.set(alias, names);
        }
        return (alias: string, fieldName: string): boolean | null => byAlias.get(alias)?.get(fieldName) ?? null;
    }

    private static component(value: number | null | undefined): number {
        const n = Math.trunc(Number(value ?? 0));
        return Number.isFinite(n) && n > 0 ? n : 0;
    }
}
